// Packages
import React, { useState, useEffect, useMemo } from "react";
import { useTheme } from "@mui/material/styles";
import { v4 as uuidv4 } from "uuid";

// Components
import { Box, Button, Typography } from "@mui/material";
import ModalContainer from "components/ModalContainer";
import { MenuSelect } from "components/Select/menuSelect";
import { SolidButton } from "components/buttons/SolidButton";

// Img
import { ReactComponent as CloseIcon } from "assets/images/icons/icn_close.svg";

// Hook
import useAuthScope from "hooks/useAuthScope";
import useDataSourceManager from "hooks/useDataSourceManager";

// Helper
import { getReleaseNotesSendRequestParams, getReleaseNoteCleanPayload, initTargetFWList } from "../../helper";

const ReleaseNotesModal = ({
  isError,
  setIsError,
  messages,
  setMessages,
  selectedFW,
  setSelectedFW,
  isOpenReleaseModal,
  setIsOpenReleaseModal,
  isOpenSupportListModal
}) => {
  const [uuids] = useState(() => ({
    releaseNotes: uuidv4(),
  }));
  const theme = useTheme();
  const { dataMap, getData, sendRequest } = useDataSourceManager();
  const { auth, isAuthReady } = useAuthScope();
  const [isLoading, setIsLoading] = useState(false);

  const [releaseNotes, setReleaseNotes] = useState(null);
  const [targetFWList, setTargetFWList] = useState([]);
  const selectedTargetFirmware = useMemo(() => targetFWList ? (targetFWList.find(targetFW => targetFW.isSelected) || targetFWList[0]) : null, [targetFWList]);

  const { serialName, version } = useMemo(() => {
    return {
      serialName: selectedTargetFirmware?.serialName ?? selectedFW?.serialName ?? selectedFW?.seriesName ?? "",
      version: selectedTargetFirmware?.version ?? ""
    }
  }, [selectedTargetFirmware, selectedFW]);

  const fetchTableData = () => {
    if (isAuthReady && selectedTargetFirmware?.hasReleaseNote) {
      const { apiKey, restfulParams } = getReleaseNotesSendRequestParams({ auth });
      const payload = getReleaseNoteCleanPayload({ selectedTargetFirmware });

      sendRequest(
        apiKey,
        uuids.releaseNotes,
        restfulParams,
        payload,
      );
    }
  }

  useEffect(() => {
    fetchTableData();
  }, [isAuthReady, selectedTargetFirmware?.id]);

  useEffect(() => {
    const targetFWList = initTargetFWList({
      selectedFW,
      isFromSupportListModal: isOpenSupportListModal
    });
    setTargetFWList(targetFWList);

    return () => setSelectedFW(null);
  }, [selectedFW])

  useEffect(() => {
    const response = getData(uuids.releaseNotes);
    const isLoading = !response;
    const isRespError = !!response?.error || response?.code >= 400;
    const isRespSuccess = !!response?.data || !!response?.result;

    if (isLoading) {
      setIsLoading(true);
      return;
    }

    if (isRespError) {
      setIsLoading(false);
      setIsError(true);
      setMessages({
        ...messages,
        error: response?.message || ""
      });

      return;
    }

    if (isRespSuccess) {
      const responseData = response.data || response.result;
      setReleaseNotes(responseData);

      setIsLoading(false);
    }

  }, [dataMap])

  return (
    <ModalContainer display={isOpenReleaseModal} modalWidth={"850px"}>

      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>

        <Typography
          sx={{
            color: theme.palette.primary.main,
            fontSize: theme.typography.customFontSize[5],
            fontWeight: theme.typography.customFontWeight[3],
          }}>
          {`${serialName} Firmware Version ${version} Release Notes`}
        </Typography>

        <Button
          sx={{
            backgroundColor: theme.palette.primary.transparent,
            boxShadow: "none",
            "&:hover": {
              backgroundColor: theme.palette.primary.transparent,
            },
          }}
          onClick={() => setIsOpenReleaseModal(false)}
        >
          <CloseIcon />
        </Button>

      </Box>

      {
        isOpenSupportListModal &&
        selectedTargetFirmware?.label &&
        <Box>
          <MenuSelect
            placeholder="Select a Target FW Ver."
            width="300px"
            value={selectedTargetFirmware?.value}
            options={targetFWList}
            onChange={fw => {
              setTargetFWList(prevList => {
                return prevList.map(loopFwItem => ({
                  ...loopFwItem,
                  isSelected: loopFwItem.version === fw.version && loopFwItem.id === fw.id
                }))
              })
            }}
          />
        </Box>
      }

      {/* Release note */}
      {
        selectedTargetFirmware?.hasReleaseNote &&
        <Box sx={{ marginTop: "20px" }}>
          <Box>
            <Typography
              component="div"
              sx={{
                color: theme.palette.primary.black,
                fontSize: theme.typography.customFontSize[2],
              }}
              dangerouslySetInnerHTML={{ __html: releaseNotes?.content }}
            >
            </Typography>
          </Box>
        </Box>
      }

      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", marginTop: "50px" }}>
        <Box margin="0 10px">
          <SolidButton
            width="100px"
            height="32px"
            onClick={() => setIsOpenReleaseModal(false)}
          >
            Close
          </SolidButton>
        </Box>
      </Box>

    </ModalContainer>
  );
};

export default ReleaseNotesModal;